import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { MessageSquareText, MessageCircle, Mail, Phone, Facebook, Menu, X, ChevronRight } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "../ui/sheet";
import { useSite } from "../../context/SiteContext";
import { links } from "../../lib/api";

export const NAV = [
  { label: "We Buy in Bulk", to: "/bulk" },
  { label: "How It Works", to: "/#how-it-works" },
  { label: "Price List", to: "/prices" },
  { label: "FAQs", to: "/#faqs" },
  { label: "Reviews", to: "/#testimonials" },
  { label: "Contact Us", to: "/#contact" },
  { label: "About", to: "/#about" },
];

export const useNavTo = () => {
  const navigate = useNavigate();
  const location = useLocation();
  return (to) => {
    if (to.startsWith("/#")) {
      const id = to.slice(2);
      if (location.pathname === "/") {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      } else {
        navigate("/");
        setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 350);
      }
    } else {
      navigate(to);
      window.scrollTo(0, 0);
    }
  };
};

export const Logo = ({ className = "h-10" }) => (
  <img src="/logo.png" alt="StripWise" className={`${className} w-auto select-none`} draggable={false} />
);

export const LangToggle = ({ mobile = false }) => {
  const { lang, setLang } = useSite();
  return (
    <div className={`flex items-center rounded-full border border-[#dbeee9] bg-white p-0.5 ${mobile ? "text-[12px]" : "text-[13px]"} font-bold`} role="group" aria-label="Language" data-testid={mobile ? "lang-toggle-mobile" : "lang-toggle"}>
      {["en", "es"].map((l) => (
        <button key={l} onClick={() => setLang(l)} aria-pressed={lang === l} className={`rounded-full ${mobile ? "px-2.5 py-1.5" : "px-3 py-1.5"} uppercase transition-colors ${lang === l ? "bg-[#0e8c7f] text-white" : "text-[#1f3a36] hover:text-[#0e8c7f]"}`} data-testid={`lang-${l}${mobile ? "-mobile" : ""}`}>
          {l}
        </button>
      ))}
    </div>
  );
};

const Header = () => {
  const { content, openChat, t } = useSite();
  const [open, setOpen] = useState(false);
  const go = useNavTo();
  const l = links(content);

  return (
    <header className="sticky top-0 z-40" data-testid="site-header">
      <div className="flex items-center justify-center gap-1.5 whitespace-nowrap bg-[#12302c] px-2 py-1.5 text-[11px] font-semibold text-white min-[400px]:text-[12px] xl:hidden" data-testid="topbar-email-mobile">
        <Mail size={12} className="shrink-0 text-[#5fd4c1]" />
        <a href={l.mail}>{content?.email}</a>
        {content?.email2 && <><span className="text-white/40">·</span><a href={l.mail2} data-testid="topbar-email2-mobile">{content.email2}</a></>}
      </div>
      <div className="hidden md:block bg-[#12302c] text-[13.5px] text-white/90">
        <div className="mx-auto flex h-[44px] max-w-[1346px] items-center justify-between px-6 lg:px-0 lg:w-[86%]">
          <span className="flex min-w-0 items-center gap-4 overflow-hidden pr-4 text-white/75"><span className="truncate">{content?.topbar_text}</span><Link to="/track" className="shrink-0 whitespace-nowrap rounded-full bg-white/10 px-3 py-1 text-[12.5px] font-semibold text-white hover:bg-white/20" data-testid="topbar-track">{t("Track My Payout")}</Link></span>
          <div className="flex shrink-0 items-center gap-5 whitespace-nowrap">
            <a href={l.sms} className="flex items-center gap-1.5 hover:text-[#7ce3d2] transition-colors" data-testid="topbar-phone">
              <MessageSquareText size={14} className="text-[#5fd4c1]" /> {content?.phone_display}
            </a>
            <a href={l.wa} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 hover:text-[#7ce3d2] transition-colors" data-testid="topbar-whatsapp">
              <MessageCircle size={14} className="text-[#5fd4c1]" /> WhatsApp <span>{content?.whatsapp_display}</span>
            </a>
            <a href={l.mail} className="hidden xl:flex items-center gap-1.5 hover:text-[#7ce3d2] transition-colors" data-testid="topbar-email">
              <Mail size={14} className="text-[#5fd4c1]" /> {content?.email}
            </a>
            {content?.email2 && <a href={l.mail2} className="hidden xl:flex items-center gap-1.5 hover:text-[#7ce3d2] transition-colors" data-testid="topbar-email2">
              <Mail size={14} className="text-[#5fd4c1]" /> {content.email2}
            </a>}
            <a href={content?.facebook} target="_blank" rel="noreferrer" aria-label="Facebook" className="hover:text-[#7ce3d2]">
              <Facebook size={14} className="text-[#5fd4c1]" />
            </a>
          </div>
        </div>
      </div>

      <div className="border-b border-[#e8f1ef] bg-white/95 backdrop-blur">
        <div className="mx-auto flex h-[72px] max-w-[1346px] items-center justify-between gap-4 px-5 lg:px-0 lg:w-[86%]">
          <Link to="/" onClick={() => window.scrollTo(0, 0)} className="shrink-0" data-testid="header-logo">
            <Logo className="h-9 md:h-10" />
          </Link>

          <nav className="hidden xl:flex items-center gap-[14px] 2xl:gap-[22px] text-[14.5px] 2xl:text-[15px] text-[#1f3a36]">
            <button
              onClick={() => openChat(t("Hi! I'd like to sell my strips."))}
              className="whitespace-nowrap rounded-full bg-[#f59e0b] px-4 py-2.5 text-[14.5px] font-semibold text-white shadow-[0_6px_18px_-6px_rgba(245,158,11,.7)] hover:bg-[#e08e06] transition-colors"
              data-testid="nav-sell-strips"
            >
              {t("Sell Your Strips")}
            </button>
            {NAV.map((n) => (
              <button key={n.label} onClick={() => go(n.to)} className="relative whitespace-nowrap py-1 font-medium hover:text-[#0e8c7f] transition-colors after:absolute after:-bottom-0.5 after:left-0 after:h-[2px] after:w-0 after:bg-[#0e8c7f] after:transition-[width] hover:after:w-full" data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}>
                {t(n.label)}
              </button>
            ))}
          </nav>

          <div className="hidden xl:flex items-center gap-3">
            <a href={l.tel} className="hidden 2xl:flex items-center gap-2 whitespace-nowrap text-[15px] font-medium text-[#0f2e2a] hover:text-[#0e8c7f]" data-testid="header-call">
              <Phone size={16} className="text-[#0e8c7f]" /> {content?.phone_display}
            </a>
            <LangToggle />
            <Link to="/admin" className="hidden 2xl:block whitespace-nowrap rounded-full border-2 border-[#0e8c7f] px-4 py-2 text-[14.5px] font-semibold text-[#0e8c7f] hover:bg-[#0e8c7f] hover:text-white transition-colors" data-testid="header-signin">
              {t("Sign In")}
            </Link>
          </div>

          <div className="flex xl:hidden items-center gap-2">
            <LangToggle mobile />
            <a href={l.tel} className="flex h-10 w-10 items-center justify-center rounded-full bg-[#e6f7f3] text-[#0e8c7f]" aria-label="Call">
              <Phone size={18} />
            </a>
            <button onClick={() => setOpen(!open)} className="flex h-10 w-10 items-center justify-center rounded-full border border-[#dbeee9] text-[#0f2e2a]" data-testid="mobile-menu-toggle" aria-label="Menu">
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>

        <Sheet open={open} onOpenChange={setOpen}>
          <SheetContent side="right" className="w-[86vw] max-w-[380px] overflow-y-auto p-0" data-testid="mobile-menu">
            <SheetTitle className="sr-only">Menu</SheetTitle>
            <div className="flex items-center border-b border-[#eef3f2] px-6 py-5">
              <Logo className="h-8" />
            </div>
            <div className="px-6 pt-5">
              <button onClick={() => { setOpen(false); openChat(t("Hi! I'd like to sell my strips.")); }} className="w-full rounded-full bg-[#f59e0b] py-3.5 text-[16px] font-semibold text-white shadow-[0_8px_20px_-8px_rgba(245,158,11,.8)] active:scale-[.99] transition-transform" data-testid="mobile-sell-strips">
                {t("Sell Your Strips")}
              </button>
            </div>
            <nav className="mt-3 px-6">
              {NAV.map((n) => (
                <button
                  key={n.label}
                  onClick={() => { setOpen(false); setTimeout(() => go(n.to), 200); }}
                  className="flex w-full items-center justify-between border-b border-[#edf2f1] py-[18px] text-left text-[17px] font-medium text-[#1f3a36] hover:text-[#0e8c7f] transition-colors"
                  data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {t(n.label)}
                  <ChevronRight size={18} className="text-[#a9bbb7]" />
                </button>
              ))}
            </nav>
            <div className="px-6 pt-4">
              <Link to="/track" onClick={() => setOpen(false)} className="flex w-full items-center justify-between border-b border-[#edf2f1] py-[18px] text-[17px] font-medium text-[#1f3a36]" data-testid="mobile-nav-track">{t("Track My Payout")}<ChevronRight size={18} className="text-[#a9bbb7]" /></Link>
            </div>
            <div className="space-y-3 px-6 py-6">
              <a href={l.tel} className="flex items-center gap-3 rounded-2xl bg-[#f4fbf9] px-4 py-3.5 text-[15px] font-semibold text-[#0f2e2a]">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#0e8c7f] text-white"><Phone size={16} /></span>
                <span>{t("Call / Text")}<br /><span className="text-[13.5px] font-medium text-[#5b6d6a]">{content?.phone_display}</span></span>
              </a>
              <a href={l.wa} target="_blank" rel="noreferrer" className="flex items-center gap-3 rounded-2xl bg-[#f4fbf9] px-4 py-3.5 text-[15px] font-semibold text-[#0f2e2a]">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#25a35a] text-white"><MessageCircle size={16} /></span>
                <span>WhatsApp<br /><span className="text-[13.5px] font-medium text-[#5b6d6a]">{content?.whatsapp_display}</span></span>
              </a>
              <a href={l.mail} className="flex items-center gap-3 rounded-2xl border border-[#bfe7de] bg-white px-4 py-3.5 text-[15px] font-semibold text-[#0f2e2a]" data-testid="mobile-menu-email">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#0f2e2a] text-white"><Mail size={16} /></span>
                <span className="min-w-0">{t("Email")}<br /><span className="block truncate text-[13.5px] font-semibold text-[#0b6f65]">{content?.email}</span></span>
              </a>
              {content?.email2 && <a href={l.mail2} className="flex items-center gap-3 rounded-2xl border border-[#bfe7de] bg-white px-4 py-3.5 text-[15px] font-semibold text-[#0f2e2a]" data-testid="mobile-menu-email2">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#0f2e2a] text-white"><Mail size={16} /></span>
                <span className="min-w-0">{t("Email")}<br /><span className="block truncate text-[13.5px] font-semibold text-[#0b6f65]">{content.email2}</span></span>
              </a>}
              <Link to="/admin" onClick={() => setOpen(false)} className="block w-full rounded-full border-2 border-[#0e8c7f] py-3 text-center text-[15px] font-semibold text-[#0e8c7f]">{t("Sign In")}</Link>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
};

export default Header;
